// Every word the site response screen says of its own accord, in both languages.
//
// The screen opens in the language its invitation mail was written in (the API sends it as
// `defaultLang`, read off the send log) and the toggle in the header switches the whole
// page — header, password screen, consent notice, form chrome, buttons, errors and the
// confirmation dialog alike. A half-translated page is worse than an untranslated one:
// the reader stops trusting which half was meant for them.
//
// What is NOT here is the study's own content — narrative, question text, checklist
// options, specify labels. Those are authored once per study, in one language, and the
// toggle cannot invent the other one; they render as written whichever side is selected.
export const SITE_LANGS = [
  { key: "ko", label: "한국어" },
  { key: "en", label: "English" },
];

export const DEFAULT_SITE_LANG = "ko";

// Anything unrecognized (a legacy send with no recorded language, a hand-edited URL) falls
// back to Korean — the same default the API applies.
export function normalizeSiteLang(v) {
  return v === "en" ? "en" : DEFAULT_SITE_LANG;
}

// Dates are the one value formatted rather than translated, so they follow the toggle too.
const LOCALE = { ko: "ko-KR", en: "en-US" };

const TEXT = {
  ko: {
    locale: LOCALE.ko,
    brand: "BeccoCRO BeTree System — 실시기관 사전 타당성 조사",
    confidential: "대외비",
    langLabel: "언어",

    studyCode: "연구 코드",
    site: "기관",
    pi: "연구책임자(PI)",

    loading: "설문을 불러오는 중...",

    lockedHeading: "접속 비밀번호 필요",
    lockedIntro: "초대 메일에 안내된 접속 비밀번호를 입력하시면 설문이 열립니다.",
    passwordPlaceholder: "접속 비밀번호",
    verify: "접속",
    verifying: "확인 중...",
    wrongPassword: "비밀번호가 올바르지 않습니다.",
    verifyFailed: "확인 중 오류가 발생했습니다. 다시 시도해 주세요.",

    consentAgree: "위 개인정보 수집·이용에 동의합니다.",
    consentRequired: "[필수]",
    consentSubmit: "동의하고 설문 시작",
    consentBusy: "처리 중...",
    consentHint:
      "동의하지 않으시면 설문에 응답하실 수 없습니다. 동의 일시와 접속 IP가 함께 기록됩니다.",
    consentFail: "동의 처리 중 오류가 발생했습니다. 다시 시도해 주세요.",

    expiredTitle: "링크 유효기간이 지났습니다.",
    expiredOn: (d) => `만료일: ${d}`,
    expiredContact: "새 링크가 필요하시면 담당 CRA에게 연락해 주세요.",
    // 담당자가 링크를 다시 열어준 뒤에도 이 화면이 그대로 남아 있으면, 읽는 사람은 새로고침을
    // 해야 한다는 것을 알 길이 없다.
    expiredRetry: "다시 확인",
    expiredRetryBusy: "확인 중...",

    invalidTitle: "유효하지 않거나 알 수 없는 링크입니다.",
    invalidContact: "주소를 다시 확인하시거나 담당 CRA에게 연락해 주세요.",

    submittedTitle: "설문이 제출되었습니다",
    submittedBody: "감사합니다. 제출하신 응답은 검토 중입니다.",

    overview: "연구 개요",
    questions: "설문 문항",
    section: (no, title) => `섹션 ${no}${title ? `: ${title}` : ""}`,
    colNo: "번호",
    colQuestion: "문항",
    colAnswer: "응답",
    requiredField: "필수 항목입니다.",
    yn: { Y: "예", N: "아니오", NA: "해당 없음" },

    save: "임시 저장",
    saving: "저장 중...",
    saved: "저장됨 ✓",
    saveFailed:
      "저장에 실패했습니다. 입력한 답변은 화면에 남아 있지만 아직 서버에 저장되지 않았습니다. " +
      "네트워크 상태를 확인한 뒤 임시 저장을 다시 눌러 주세요. 저장되기 전에는 최종 제출할 수 없습니다.",
    missingRequired: (n) => `필수 항목 ${n}개가 입력되지 않았습니다.`,
    submitFinal: "최종 제출",
    submitting: "제출 중...",
    submitFailed: "제출에 실패했습니다.",

    confirmTitle: "최종 제출",
    confirmBody:
      "제출하면 설문이 닫히고 답변을 더 이상 수정하실 수 없습니다. 응답이 모두 작성되었는지 확인해 주세요.",
    cancel: "취소",
    confirmSubmit: "제출",

    footer:
      "본 문서는 대외비이며 수신인 본인만 열람할 수 있습니다. 무단 사용·공개·배포를 금합니다.",
  },

  en: {
    locale: LOCALE.en,
    brand: "BeccoCRO BeTree System - Site Feasibility Questionnaire",
    confidential: "CONFIDENTIAL",
    langLabel: "Language",

    studyCode: "Study Code",
    site: "Site",
    pi: "PI",

    loading: "Loading questionnaire...",

    lockedHeading: "Access Password Required",
    lockedIntro:
      "Please enter the access password from your invitation email to open the questionnaire.",
    passwordPlaceholder: "Access password",
    verify: "Access",
    verifying: "Verifying...",
    wrongPassword: "The password is incorrect.",
    verifyFailed: "Something went wrong while verifying. Please try again.",

    consentAgree: "I agree to the collection and use of personal information described above.",
    consentRequired: "[Required]",
    consentSubmit: "Agree and Start",
    consentBusy: "Submitting...",
    consentHint:
      "You cannot respond to the questionnaire without consenting. The date, time and IP address of your consent are recorded.",
    consentFail: "Something went wrong while recording your consent. Please try again.",

    expiredTitle: "This link has expired.",
    expiredOn: (d) => `Expired on ${d}`,
    expiredContact: "Please contact the CRA to request a new link.",
    expiredRetry: "Check again",
    expiredRetryBusy: "Checking...",

    invalidTitle: "Invalid or unknown link.",
    invalidContact: "Please verify the URL or contact the CRA.",

    submittedTitle: "Questionnaire Submitted",
    submittedBody: "Thank you. Your responses have been submitted and are under review.",

    overview: "Study Overview",
    questions: "Feasibility Questions",
    section: (no, title) => `Section ${no}${title ? `: ${title}` : ""}`,
    colNo: "No.",
    colQuestion: "Question",
    colAnswer: "Your Answer",
    requiredField: "This question is required.",
    yn: { Y: "Yes", N: "No", NA: "N/A" },

    save: "Save Progress",
    saving: "Saving...",
    saved: "Saved ✓",
    saveFailed:
      "Saving failed. Your answers are still on screen but have not reached the server yet. " +
      "Check your connection and press Save Progress again. You cannot submit until they are saved.",
    missingRequired: (n) => `${n} required question${n === 1 ? " is" : "s are"} unanswered.`,
    submitFinal: "Submit Final Response",
    submitting: "Submitting...",
    submitFailed: "Submit failed",

    confirmTitle: "Submit Final Response",
    confirmBody:
      "Once submitted, the questionnaire is closed and your answers can no longer be edited. Please make sure your responses are complete.",
    cancel: "Cancel",
    confirmSubmit: "Submit",

    footer:
      "This document is confidential and intended solely for the use of the addressed recipient. " +
      "Unauthorized use, disclosure or distribution is prohibited.",
  },
};

export function siteText(lang) {
  return TEXT[normalizeSiteLang(lang)];
}
